import { useEffect, useMemo, useState } from "react";
import { mapDepartmentsForDropdown, mapRolesForDropdown } from "@/helpers/Remappers";
import Tooltip from "@/tooltip";
import {
	ArrowsRightLeftIcon,
	ClipboardIcon,
	PencilSquareIcon,
	PlusIcon,
	UserPlusIcon,
} from "@heroicons/react/20/solid";
import cleanDeep from "clean-deep";
import { addYears, format, parse, subYears } from "date-fns";
import { merge, setWith } from "lodash";
import DatePicker from "react-datepicker";
import { NotificationManager } from "react-notifications";
import { connect } from "react-redux";
import { Navigate } from "react-router-dom";
import Select from "react-select";
import { Actions as EMPLOYEEACTIONS } from "../../../store/actions/employeeActions";
import { DateInputFormat, handleRawDate } from "../../common/DateComponents";
import Occupations from "../occupations/Occupations";
import { copyToClipboard } from "@/helpers/ClipboardHelpers";

const stringToDate = (dateString) => {
	if (!dateString) return new Date();
	return parse(dateString, "yyyy-MM-dd", new Date());
};

const dateToString = (date) => {
	if (!date) return undefined;
	return format(date, "yyyy-MM-dd");
};

const RoleDetails = ({
	role,
	departmentOptions,
	confirmModal,
	openEngagementDateModal,
	openTerminationDateModal,
	engagementDate,
	terminationDate,
	disabled,
	userPermissions,
	forNewEmployee,
	prevPage,
	nextPage,
	confirmChange,
	gradeRoles,
	forReview,
	reviewEmploymentRole,
	forReviewAgreement,
	notification,
	rehireEmployee,
	agreement,
	jurisdiction,
	setRole,
	roleOptions,
	departmentHistory,
	lastRole,
	deleteRoleModal,
	canEditLastRole,
	isAdmin,
	deleteDepartmentModal,
	updateDepartmentModal,
	setDepartment,
	selectedDepartment,
	lastDepartment,
	hasMultiRole,
	occupation,
}) => {
	const [redirectToPeople, setRedirectToPeople] = useState();
	const [data, setData] = useState({});
	const updateField = (field, value) => {
		// console.debug(field, value)
		if (!field) return;
		if (field.includes("[")) field = setWith({}, field, value, Object);

		const newData = merge(data, { ...(typeof field == "object" ? field : { [field]: value }) });

		setData({ ...newData });
	};

	const roleID = useMemo(() => {
		return String(role?.id);
	}, [role?.id]);

	const employeeDepartmentID = useMemo(() => {
		// const departmentID = departmentHistory?.find(item => item.value == selectedDepartment)?.department
		return String(selectedDepartment);
	}, [selectedDepartment]);

	useEffect(() => {
		const hasRole = role && Object.keys(cleanDeep(role))?.length > 0;
		if (!hasRole) {
			setData({
				id: null,
				occupation: occupation,
				start: null,
				engagementDate: null,
				terminationDate: null,
				role: {
					name: "",
					description: "",
				},
			});
		} else {
			setData({
				id: role?.id,
				occupation: role?.occupation,
				start: forReview ? role?.start : undefined,
				engagementDate: forReview ? role?.engagementDate : undefined,
				terminationDate: forReview ? role?.terminationDate : undefined,
				role: {
					name: gradeRoles
						? gradeRoles?.find((gradeRole) => gradeRole.value == role?.name)?.value
						: role?.name,
					description: role?.description,
				},
				department: role?.department,
			});
		}
	}, [roleID, gradeRoles, occupation]);

	const onSubmit = (e) => {
		e.preventDefault();
		e.stopPropagation();

		if (!data.role?.name && ["MT"].includes(jurisdiction)) {
			NotificationManager.error("Role cannot be empty", "Saving Role");
			return;
		}

		const mappedData = {
			input: cleanDeep(data, { emptyStrings: false, emptyArrays: false, emptyObjects: false }),
			status: true,
			formType: "Role",
			previousDate: role?.startDate || engagementDate,
		};

		if (forReview) {
			console.debug({
				engagementDate: data.engagementDate,
				terminationDate: data.terminationDate,
				start: data.start,
			});
			const engagementDate = data.engagementDate ? dateToString(stringToDate(data.engagementDate)) : undefined;

			if (typeof data.terminationDate === "string" && ["null", "undefined"].includes(terminationDate)) {
				data.terminationDate = dateToString(stringToDate(data.terminationDate));
			}

			const terminationDate = data.terminationDate;

			const start = data.start ? dateToString(stringToDate(data.start)) : null;
			const { originalDate } = { ...forReview };
			reviewEmploymentRole(
				{ engagementDate, terminationDate, agreement: forReviewAgreement, start, originalDate, notification },
				mappedData,
			);
		}

		confirmModal(mappedData);

		if (forNewEmployee) {
			confirmChange(dateToString(engagementDate), true, nextPage);
		}
	};

	if (redirectToPeople) return <Navigate to={"/organisation/people"} />;

	return (
		<form
			onSubmit={onSubmit}
			style={{ paddingBottom: forNewEmployee ? undefined : "4rem" }}
			className="px-8"
		>
			{!forNewEmployee && (hasMultiRole ? !occupation : true) ? (
				<>
					<div className="col-xs-12 form-section">
						<div className="col-xs-12 section-header">
							<h1>Agreement Dates</h1>
						</div>
						{!forNewEmployee && !forReview && (
							<div className="col-xs-12 form-container">
								<div className="col-xs-3 pl-0">
									<div className="form-material">
										<label htmlFor="material-text">
											<Tooltip content="To change the Date of Employment, click on the edit icon to the right, and insert the new employment date within the modal.">
												Engagement Date*
											</Tooltip>
										</label>
										<input
											id="effective[start][0]"
											type="text"
											name="effective[start][0]"
											required="required"
											disabled="disabled"
											className="form-control form-input"
											value={engagementDate && format(new Date(engagementDate), "do MMM yyyy")}
										/>
									</div>
								</div>
								<div className="col-xs-3 pl-0">
									<div className="form-material">
										{!disabled && (
											<>
												<div
													className="btn btn-xs btn-default"
													type="button"
													data-toggle="modal"
													data-period="Start"
													data-input="effective[start][0]"
													onClick={() => openEngagementDateModal()}
												>
													<PencilSquareIcon
														className="h-5 w-5 text-dark-gray"
														style={{ marginTop: "3px" }}
													/>
												</div>
												<span
													style={{
														fontSize: "10px",
														display: "inline-block",
														marginLeft: "10px",
													}}
												>
													First Employed on <br />
												</span>
											</>
										)}
									</div>
								</div>
								<div className="col-xs-3">
									{terminationDate && (
										<div className="form-material">
											<label
												className="error"
												id="effective-date-error"
											/>
											<label htmlFor="material-text">
												<Tooltip
													content={
														userPermissions != "Read"
															? "To change the Date of Termination, click on the edit icon to the right, and insert the new termination date within the modal."
															: "You do not have sufficient permissions to change the date of termination."
													}
												>
													Termination Date*
												</Tooltip>
											</label>
											<input
												id="effective-date-form"
												type="text"
												required="required"
												disabled="disabled"
												className="form-control"
												value={
													terminationDate && format(new Date(terminationDate), "do MMM yyyy")
												}
											/>
										</div>
									)}
								</div>
								{userPermissions != "Read" && (
									<div className="col-xs-3 p-0">
										{!disabled && !terminationDate ? (
											<a
												className="btn btn-danger pull-right"
												type="button"
												onClick={() => openTerminationDateModal()}
											>
												Add Termination Date
											</a>
										) : (
											<div className="form-material">
												<div
													className="btn btn-xs btn-default open_edit_period"
													type="button"
													data-toggle="modal"
													data-period="Start"
													data-input="effective[start][0]"
													onClick={() => openTerminationDateModal()}
												>
													<PencilSquareIcon
														className="h-5 w-5 text-dark-gray"
														style={{ marginTop: "3px" }}
													/>
												</div>
												<div
													className="btn btn-default pull-right"
													type="button"
													style={{ marginTop: 15 }}
													onClick={() => {
														rehireEmployee(agreement);
														setRedirectToPeople(true);
													}}
												>
													<UserPlusIcon
														style={{ height: "1.5em", float: "left", marginRight: 5 }}
													/>
													<div style={{ float: "left" }}>Rehire</div>
												</div>
											</div>
										)}
									</div>
								)}
							</div>
						)}
						{forReview || forNewEmployee ? (
							<div className="col-xs-12 form-container">
								<div className="col-xs-4 pl-0 form-material">
									<label htmlFor="material-text">Effective Role Date</label>
									<DatePicker
										autoComplete="off"
										dateFormat="dd MMM yyyy"
										minDate={subYears(new Date(), 30)}
										maxDate={addYears(new Date(), 10)}
										className="form-control form-input"
										onChange={(date) => {
											updateField("start", date ? format(date, "yyyy-MM-dd") : undefined);
										}}
										onChangeRaw={(e) => {
											handleRawDate(e.target.value, (date) => {
												updateField("start", date ? format(date, "yyyy-MM-dd") : undefined);
											});
										}}
										strictParsing
										selected={data.start && parse(data.start, "yyyy-MM-dd", new Date())}
										customInput={<DateInputFormat />}
										wrapperClassName="full-width"
									/>
								</div>

								{engagementDate ? (
									<div className="col-xs-4 pl-0 form-material">
										<label htmlFor="material-text">Engagement Date*</label>
										<DatePicker
											className="form-control form-input"
											autoComplete="off"
											dateFormat="dd MMM yyyy"
											minDate={subYears(new Date(), 100)}
											maxDate={addYears(new Date(), 100)}
											name="engagementDate"
											selected={
												data.engagementDate &&
												parse(data.engagementDate, "yyyy-MM-dd", new Date())
											}
											onChange={(date) => {
												updateField(
													"engagementDate",
													date ? format(date, "yyyy-MM-dd") : undefined,
												);
											}}
											onChangeRaw={(e) => {
												handleRawDate(e.target.value, (date) => {
													updateField(
														"engagementDate",
														date ? format(date, "yyyy-MM-dd") : undefined,
													);
												});
											}}
											customInput={<DateInputFormat />}
											wrapperClassName="full-width"
										/>
									</div>
								) : null}

								<div className="col-xs-4 pl-0">
									{terminationDate ? (
										<div className="form-material">
											<label htmlFor="material-text">Termination Date*</label>
											<DatePicker
												className="form-control form-input"
												autoComplete="off"
												dateFormat="dd MMM yyyy"
												minDate={subYears(new Date(), 100)}
												maxDate={addYears(new Date(), 100)}
												name="terminationDate"
												selected={
													data.terminationDate &&
													parse(data.terminationDate, "yyyy-MM-dd", new Date())
												}
												onChange={(date) => {
													updateField(
														"terminationDate",
														date ? format(date, "yyyy-MM-dd") : undefined,
													);
												}}
												onChangeRaw={(e) => {
													handleRawDate(e.target.value, (date) => {
														updateField(
															"terminationDate",
															date ? format(date, "yyyy-MM-dd") : undefined,
														);
													});
												}}
												customInput={<DateInputFormat />}
												wrapperClassName="full-width"
											/>
										</div>
									) : null}
								</div>
							</div>
						) : null}
						<div className="clearfix" />
					</div>
				</>
			) : null}

			<Occupations
				{...{
					forNewEmployee,
					forReview,
					roleOptions,
					isAdmin,
					occupation,
					lastRole,
					roleID,
					hasMultiRole,
					setRole,
					deleteRoleModal,
					disabled,
					data,
				}}
			/>

			<RoleSelector
				{...{
					forNewEmployee,
					forReview,
					roleOptions,
					isAdmin,
					hasMultiRole,
					occupation,
					lastRole,
					roleID,
					setRole,
					deleteRoleModal,
					disabled,
				}}
			/>

			<RoleSettings
				{...{
					hasMultiRole,
					forNewEmployee,
					forReview,
					occupation,
					gradeRoles,
					role,
					data,
					departmentOptions,
					updateField,
					disabled,
					canEditLastRole,
					jurisdiction,
				}}
			/>

			<RoleButtonManager
				{...{
					forNewEmployee,
					forReview,
					roleID,
					hasMultiRole,
					occupation,
					lastRole,
					nextPage,
					prevPage,
					disabled,
					jurisdiction,
				}}
			/>

			{!forNewEmployee && <div className="clearfix py-10" />}

			<DepartmentSettings
				{...{
					hasMultiRole,
					occupation,
					forNewEmployee,
					forReview,
					departmentHistory,
					employeeDepartmentID,
					setDepartment,
					isAdmin,
					disabled,
					lastDepartment,
					deleteDepartmentModal,
					updateDepartmentModal,
				}}
			/>

			<div className="clearfix" />
		</form>
	);
};

function RoleSelector({
	forNewEmployee,
	forReview,
	roleOptions,
	isAdmin,
	occupation,
	lastRole,
	roleID,
	hasMultiRole,
	setRole,
	deleteRoleModal,
	disabled,
}) {
	if (forNewEmployee || forReview || (hasMultiRole && !occupation)) return null;

	return (
		<div className="col-xs-12 form-section">
			<div className="col-xs-2 section-header">
				<h1>Role</h1>
			</div>
			<div className="col-xs-10">
				<Select
					options={roleOptions}
					value={roleOptions?.find((s) => s.value === roleID)}
					onChange={({ value }) => {
						setRole(value);
					}}
				/>
				<div style={{ position: "absolute", top: 6, right: 65 }}>
					{isAdmin ? (
						<>
							<button
								className="btn-default btn-xs btn pull-right"
								style={{ marginTop: 1, marginLeft: 5 }}
								type="button"
								onClick={() => {
									copyToClipboard(roleID);
								}}
							>
								<ClipboardIcon
									className="h-5 w-5"
									style={{ marginTop: "3px" }}
								/>
							</button>
						</>
					) : null}
					{lastRole === roleID && !disabled && roleOptions?.length > 1 ? (
						<>
							<button
								className="btn btn-danger pull-right"
								type="button"
								style={{
									fontSize: 12,
									fontWeight: "normal",
									padding: "4px 8px",
								}}
								onClick={(e) => {
									e.stopPropagation();
									e.preventDefault();
									deleteRoleModal();
								}}
							>
								Delete
							</button>
						</>
					) : null}
				</div>
			</div>
			<div className="clearfix" />
		</div>
	);
}

function RoleSettings({
	hasMultiRole,
	forNewEmployee,
	forReview,
	occupation,
	gradeRoles,
	data,
	departmentOptions,
	updateField,
	disabled,
	canEditLastRole,
	jurisdiction,
}) {
	if (hasMultiRole && !occupation) return null;

	return (
		<div className="col-xs-12 form-section">
			<div className="col-xs-12 section-header">
				<h1>Role Details</h1>
			</div>

			<div className="col-xs-12 form-container">
				<div className=" col-xs-3 pl-0">
					<div className="form-material">
						{gradeRoles ? (
							<Select
								isDisabled={disabled}
								options={gradeRoles}
								value={gradeRoles?.find((gradeRole) => gradeRole.value == data?.role?.name)}
								placeholder="Choose a Grade Role"
								isClearable={["UK"].includes(jurisdiction)}
								onChange={(selected) => {
									updateField("role[name]", selected?.value || null);
								}}
							/>
						) : (
							<input
								id="role[name]"
								className="form-control form-input"
								type="text"
								name="role[name]"
								value={data?.role?.name}
								onChange={(e) => {
									updateField(e.target.name, e.target.value);
								}}
								disabled={disabled || !canEditLastRole}
							/>
						)}
						<label htmlFor="role[name]">
							<Tooltip content="The employee role will appear within the payslip, and needs to match what is officially filed.">
								Role{["MT"].includes(jurisdiction) ? "*" : ""}
							</Tooltip>
						</label>
					</div>
					<div className="clearfix" />
				</div>

				<div className=" col-xs-9">
					<div className="form-material">
						<input
							id="role-description"
							className="form-control form-input"
							type="text"
							name="role[description]"
							value={data?.role?.description}
							onChange={(e) => {
								updateField(e.target.name, e.target.value);
							}}
							disabled={disabled || !canEditLastRole}
						/>
						<label htmlFor="role[description]">Role Description</label>
					</div>
				</div>
			</div>
			<div className="clearfix" />
			{forNewEmployee || forReview ? (
				<>
					<div className=" col-xs-12 form-container">
						<div className="col-xs-6  pl-0">
							<label htmlFor="example2-select2">Department</label>
							<Select
								isDisabled={disabled || !canEditLastRole}
								options={departmentOptions}
								name="department"
								value={departmentOptions?.find((item) => item.value == data?.department)}
								placeholder="Choose a Department"
								onChange={(selected) => {
									updateField("department", selected?.value || null);
								}}
							/>
							{/* <span className="help-block animated fadeInDown">
										To add a new department, type your department name then press
										enter.
									</span> */}
						</div>
					</div>
				</>
			) : null}
			<div className="clearfix" />
		</div>
	);
}

function RoleButtonManager({
	forNewEmployee,
	forReview,
	roleID,
	lastRole,
	hasMultiRole,
	occupation,
	nextPage,
	prevPage,
	disabled,
	jurisdiction,
}) {
	if ((disabled || (hasMultiRole && !occupation)) && !forNewEmployee) return null;
	return (
		<div className="col-xs-12 pl-0 pr-0">
			{forNewEmployee ? (
				<>
					{prevPage && (
						<button
							style={{ marginTop: 10, marginBottom: 10 }}
							className="btn btn-default"
							onClick={prevPage}
						>
							Back
						</button>
					)}
					{jurisdiction === "UK" && (
						<button
							style={{ marginTop: 10, marginLeft: "1rem" }}
							className="btn btn-default pull-right"
							onClick={nextPage}
						>
							Skip
						</button>
					)}
					<button
						className="btn btn-success pull-right"
						type="submit"
						style={{ marginTop: 10, marginBottom: 10 }}
					>
						Save &amp; Continue
					</button>
				</>
			) : null}
			{!forNewEmployee && (forReview || lastRole === roleID) ? (
				<>
					<button
						className="btn btn-success pull-right"
						type="submit"
					>
						Update Role Details
					</button>
				</>
			) : null}
			{!forNewEmployee && !forReview && !lastRole ? (
				<button
					className="btn btn-success pull-right"
					type="submit"
				>
					Save Role Details
				</button>
			) : null}
		</div>
	);
}

function DepartmentSettings({
	hasMultiRole,
	occupation,
	forNewEmployee,
	forReview,
	departmentHistory,
	employeeDepartmentID,
	setDepartment,
	isAdmin,
	disabled,
	lastDepartment,
	deleteDepartmentModal,
	updateDepartmentModal,
}) {
	// {!forNewEmployee && !forReview && departmentOptions?.length > 0 ? (
	if ((hasMultiRole && !occupation) || forNewEmployee || forReview) return null;

	return (
		<div className="col-xs-12 form-section">
			<div className="col-xs-2 section-header">
				<h1>Department</h1>
			</div>
			{!disabled ? (
				<>
					<div className="col-xs-10">
						<button
							type="button"
							className="btn btn-success pull-right"
							onClick={() => {
								updateDepartmentModal(occupation);
							}}
						>
							{departmentHistory?.length > 0 ? (
								<ArrowsRightLeftIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
							) : (
								<PlusIcon style={{ height: "1.5em", float: "left" }} />
							)}
							<div style={{ float: "left" }}>
								{departmentHistory?.length > 0 ? "Switch Department" : "Assign Department"}
							</div>
						</button>
					</div>
				</>
			) : null}
			<div className="col-xs-12 form-container">
				<Select
					options={departmentHistory}
					menuPlacement="auto"
					value={departmentHistory?.find((s) => s.value === employeeDepartmentID) || null}
					onChange={({ value }) => {
						setDepartment(value);
					}}
				/>
				<div style={{ position: "absolute", top: 6, right: 65 }}>
					{isAdmin && departmentHistory?.length > 0 ? (
						<>
							<button
								className="btn-default btn-xs btn pull-right"
								style={{ marginTop: 1, marginLeft: 5 }}
								type="button"
								onClick={() => {
									copyToClipboard(employeeDepartmentID);
								}}
							>
								<ClipboardIcon
									className="h-5 w-5"
									style={{ marginTop: "3px" }}
								/>
							</button>
						</>
					) : null}
					{!disabled && lastDepartment === employeeDepartmentID ? (
						<>
							<button
								className="btn btn-danger pull-right"
								type="button"
								style={{
									fontSize: 12,
									fontWeight: "normal",
									padding: "4px 8px",
								}}
								onClick={(e) => {
									e.stopPropagation();
									e.preventDefault();
									deleteDepartmentModal(employeeDepartmentID);
								}}
							>
								Delete
							</button>
						</>
					) : null}
				</div>
			</div>
		</div>
	);
}

const mapStateToProps = (state, ownProps) => {
	const {
		roles: agreementRoles,
		departments: agreementDepartments,
		agreementInfo,
		selectedRole: selectedRoleID,
		selectedDepartment: selectedDepartmentID,
	} = state.employee;

	const { departments: departmentList, jurisdiction } = state.company?.settings;

	const departmentOptions = departmentList?.map((dep) => ({
		value: dep.id,
		label: dep.name,
	}));

	const roles = agreementRoles?.filter((role) =>
		ownProps?.occupation ? role.occupation === ownProps.occupation : true,
	);
	const departments = agreementDepartments?.filter((department) =>
		ownProps?.occupation ? department.occupation === ownProps.occupation : true,
	);

	const { id: agreement } = { ...agreementInfo };
	const { forReview, forNewEmployee } = ownProps;
	const terminationDate = (forReview ? forReview : agreementInfo)?.terminationDate;
	const engagementDate = (forReview ? forReview : agreementInfo)?.engagementDate;

	const lastRole = roles?.[roles?.length - 1]?.id;

	const selectedRole = roles?.find((role) => role?.id === selectedRoleID) || roles?.[roles?.length - 1];

	const foundDepartment = (() => {
		let { start: roleStart } = selectedRole || {};
		if (forReview || forNewEmployee) {
			return departments?.find(({ start }) => start === roleStart);
		}
	})();

	const canEditLastRole = (selectedRoleID ? lastRole === selectedRoleID : true) || forReview;

	const role = {
		...(forReview ? forReview : selectedRole),
		department: foundDepartment?.department,
	};

	const roleOptions = mapRolesForDropdown(roles, { terminationDate, engagementDate });

	const departmentHistory = mapDepartmentsForDropdown(departments, departmentOptions);

	const lastDepartment = departmentHistory?.[departmentHistory?.length - 1]?.value;
	const selectedDepartment = selectedDepartmentID || departmentHistory?.[departmentHistory?.length - 1]?.value;

	return {
		occupations: state.employee?.agreementInfo?.occupations,
		gradeRoles: state.grades?.apply?.roles?.map((role) => ({ value: role, label: role })),
		roleOptions,
		role,
		departmentOptions,
		engagementDate: engagementDate && parse(engagementDate, "yyyy-MM-dd", new Date()),
		agreement,
		terminationDate: terminationDate && parse(terminationDate, "yyyy-MM-dd", new Date()),
		disabled:
			state?.permissions?.my?.permissions?.people == "Read" ||
			(!forReview &&
				!forNewEmployee &&
				terminationDate &&
				new Date().toISOString() > new Date(terminationDate).toISOString()),
		userPermissions: state?.permissions?.my?.permissions?.people,
		modalStatus: state?.employee?.confirmModal?.status,
		lastRole,
		canEditLastRole,
		isAdmin: state?.my?.profile?.isAdmin,
		departmentHistory,
		jurisdiction,
		selectedDepartment,
		lastDepartment,
		hasMultiRole: state.company?.settings?.hasMultiRole,
	};
};

const mapDispatchToProps = (dispatch) => ({
	openEngagementDateModal: () => dispatch(EMPLOYEEACTIONS.engagementDateModal(true)),
	openTerminationDateModal: () => dispatch(EMPLOYEEACTIONS.terminationDateModal(true)),
	deleteDepartmentModal: () => dispatch(EMPLOYEEACTIONS.deleteDepartmentModal(true)),
	deleteRoleModal: () => dispatch(EMPLOYEEACTIONS.deleteRoleModal(true)),
	updateDepartmentModal: (occupation) => dispatch(EMPLOYEEACTIONS.updateDepartmentModal(true, { occupation })),
	rehireEmployee: (agreement) => dispatch(EMPLOYEEACTIONS.rehireEmployee(agreement)),
	//setOpenModal: (status, leave) => dispatch(EMPLOYEEACTIONS.setOpenModal(status, leave)),
	confirmModal: (options) => dispatch(EMPLOYEEACTIONS.confirmModal(options)),
	confirmChange: (date, replace, onSuccess) => dispatch(EMPLOYEEACTIONS.confirmChange({ date, replace, onSuccess })),
	setRole: (roleID) => dispatch(EMPLOYEEACTIONS.setRole(roleID)),
	setDepartment: (departmentID) => dispatch(EMPLOYEEACTIONS.setDepartment(departmentID)),
	reviewEmploymentRole: (object, mappedData) => dispatch(EMPLOYEEACTIONS.reviewEmploymentRole(object, mappedData)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RoleDetails);
